import React, {useEffect, useRef, useState, useCallback} from 'react';
import {NavLink} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import styled, {css} from 'styled-components';

import config from 'utils/config';

import ButtonCore from 'components/ButtonCore';
import Icon from 'components/Icon';
import ThemeSection from 'components/ThemeSection';

import {pxToRem} from 'utils/ui/units';
import {mix, alpha} from 'utils/ui/colors';
import {
	COLLAPSED_NAVBAR_HEIGHT,
	spacing,
	breakpoints,
	maxPageWidth,
	z,
} from 'styles/globals';
import {ellipsis} from 'styles/mixins';

import useBreakpoints from 'hooks/useBreakpoints';
import Logo from 'components/Logo';
import NavBarTab from 'components/Tab/NavBarTab';
import EasyTooltip from 'components/EasyTooltip';

import {getUrlServiceHref} from 'utils/getUrlServiceHref';

import getNavigationLinks, {
	CUSTOM_LINK_LABEL_SOFT_CHAR_LIMIT,
} from './getNavigationLinks';
import collapseNavbar from './collapseNavbar';
import UserMenu from './userMenu';
import SearchForm from './searchForm';

export const PageWrapper = styled.div`
	/*
	 * Make page take up full height and allow footer
	 * to stick to the bottom of the screen even when
	 * there's not much page content
	 */
	display: flex;
	flex-direction: column;
	min-height: 100vh;

	/*
	 * Add space for fixed navigation bar
	 */
	padding-top: ${pxToRem(COLLAPSED_NAVBAR_HEIGHT * 2)};

	@media (min-width: ${breakpoints.s}) {
		padding-top: ${pxToRem(COLLAPSED_NAVBAR_HEIGHT)};
	}
`;

const Header = styled.header`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: ${z.navbars};

	color: ${p => p.theme.text};
	background-color: ${p => p.theme.background};

	transition: transform 200ms ease-in;
	will-change: transform;

	${p =>
		p.theme.globals.mainNavShadow &&
		css`
			box-shadow: ${p.theme.globals.mainNavShadow};
		`}

	${p =>
		p.isCollapsed &&
		css`
			@media (max-width: ${breakpoints.s}) {
				transform: translateY(-50%);
				transition-timing-function: ease-out;
			}
		`}
`;

const CollapsibleHeader = collapseNavbar(Header);

const HeaderContent = styled.div`
	position: relative;
	display: flex;
	flex-direction: column-reverse;

	width: 100%;
	max-width: ${pxToRem(maxPageWidth)};
	margin: 0 auto;
	padding: 0 ${spacing.xs};

	@media (min-width: ${breakpoints.xxs}) {
		padding: 0 ${spacing.s};
	}

	@media (min-width: ${breakpoints.s}) {
		padding: 0 ${spacing.l};
	}
`;

const PrimaryMenu = styled.nav`
	display: flex;
	align-self: stretch;
	height: ${pxToRem(COLLAPSED_NAVBAR_HEIGHT)};

	@media (min-width: ${breakpoints.s}) {
		position: absolute;
		top: 0;
	}
`;

const Menu = styled.ul`
	display: flex;
	width: 100%;
`;

const MenuItem = styled.li`
	display: flex;
	align-self: stretch;
	flex: 1 1 0%;

	@media (min-width: ${breakpoints.s}) {
		flex-basis: auto;
		margin-right: ${spacing.m};
	}
`;

const SearchAndUserMenu = styled.div`
	display: flex;
	justify-content: space-between;
	flex-direction: row-reverse;
	align-items: center;
	height: ${pxToRem(COLLAPSED_NAVBAR_HEIGHT)};

	@media (min-width: ${breakpoints.s}) {
		justify-content: flex-end;
		flex-direction: row;
	}
`;

const LogoWrapper = styled.div`
	position: absolute;
	top: 0;
	left: 50%;

	height: ${pxToRem(COLLAPSED_NAVBAR_HEIGHT)};
	margin-left: -${p => pxToRem(p.theme.globals.logo.width / 2)};
`;

const MobileSearchButton = styled(ButtonCore).withConfig({
	shouldForwardProp: prop => prop !== 'isSearchOpen',
})`
	padding: ${spacing.s} ${spacing.xs};

	transition: all 0.25s ease;

	${p =>
		p.isSearchOpen &&
		css`
			transform: translateY(${pxToRem(COLLAPSED_NAVBAR_HEIGHT)});
			opacity: 0;
		`}
`;

const SearchTrigger = styled(ButtonCore)`
	display: none;

	@media (min-width: ${breakpoints.s}) {
		display: flex;
		align-items: center;

		width: ${p => pxToRem(p.small ? 120 : 208)};
		height: ${pxToRem(36)};
		margin-right: ${spacing.m};
		padding-left: ${spacing.xs};

		background-color: ${p => alpha(p.theme.shade, p.theme.shadeStrength)};
		border-radius: ${p => pxToRem(p.theme.globals.borderRadius.search)};

		cursor: text !important;

		transition: all 200ms cubic-bezier(0.74, 0.01, 0.46, 0.99) 100ms;
		transform: translateZ(0);

		${p =>
			p.isSearchOpen &&
			css`
				transform: translateX(${pxToRem(-30)});
				opacity: 0;
				transition-delay: 0s;
			`}
	}

	@media (min-width: ${breakpoints.m}) {
		width: ${p => pxToRem(p.small ? 140 : 263)};
	}
`;

const SearchTriggerPlaceholder = styled.span`
	/* Aligning the fake placeholder as much as possible
	 * with the real input's placeholder */
	padding-left: 0.45rem;
	padding-right: ${spacing.xxs};

	color: ${p => alpha(p.theme.text, p.theme.textDimStrength)};

	${ellipsis}

	@media (max-width: ${breakpoints.xxs}) {
		display: none;
	}
`;

const SearchBackground = styled.div`
	position: absolute;
	top: 50%;
	bottom: 0;
	left: 0;
	right: 0;

	background-color: ${p =>
		mix(p.theme.shade)(p.theme.background, p.theme.shadeStrength)};
	opacity: 0;
	transform: scale(0);
	transform-origin: 60% center;

	transition: all 250ms cubic-bezier(0.74, 0.01, 0.46, 0.99);
	will-change: opacity, transform;

	@media (min-width: ${breakpoints.s}) {
		top: 0;
		left: auto;
		width: 40%;
	}

	${p =>
		p.isSearchOpen &&
		css`
			opacity: 1;
			transform: scale(1);
		`}
`;

const CustomLinkTab = styled(NavBarTab)`
	flex-grow: 0;
	margin-right: ${spacing.m};
`;

function useSearchOpenState() {
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const lastFocusedElement = useRef();
	const shouldFocusInputRef = useRef(false);
	const searchInputRef = useRef();

	const openSearch = useCallback(() => {
		setIsSearchOpen(prevIsOpen => {
			// Only store & set focus when the state changes
			// from closed to open, otherwise do nothing
			if (prevIsOpen === false) {
				lastFocusedElement.current = document.activeElement;
				shouldFocusInputRef.current = true;
				return true;
			} else return prevIsOpen;
		});
	}, []);

	const closeSearch = useCallback(() => {
		setIsSearchOpen(false);
		lastFocusedElement.current?.focus();
	}, []);

	useEffect(() => {
		if (shouldFocusInputRef.current) {
			searchInputRef.current?.focus();
			shouldFocusInputRef.current = false;
		}
	}, [isSearchOpen]);

	return {
		isSearchOpen,
		openSearch,
		closeSearch,
		searchInputRef,
	};
}

function Navigation() {
	const {isSearchOpen, openSearch, closeSearch, searchInputRef} =
		useSearchOpenState();
	const [t] = useTranslation();
	const {s: isDesktop} = useBreakpoints();
	const mainLinks = getNavigationLinks();

	const customLink = config.customNavBarLink;
	const hasCustomLink = customLink?.title?.trim() && customLink?.url;
	const isCustomTitleLong =
		hasCustomLink &&
		customLink.title.trim().length > CUSTOM_LINK_LABEL_SOFT_CHAR_LIMIT;

	return (
		<ThemeSection name="navbar">
			<CollapsibleHeader>
				<HeaderContent>
					<LogoWrapper>
						<Logo
							id="topbar_logo"
							link="/home"
							alt={t('hub_title')}
						/>
					</LogoWrapper>
					<PrimaryMenu aria-label={t('main_navigation')}>
						<Menu>
							{mainLinks.map(
								item =>
									!(isDesktop && item.hideOnDesktop) && (
										<MenuItem key={item.title}>
											<NavBarTab
												as={item.link ? NavLink : 'a'}
												to={item.link}
												href={item.href}
												id={item.id}
												icon={item.icon}
												isActive={item.isActive}
												activeClassName="is-active"
											>
												{item.title}
											</NavBarTab>
										</MenuItem>
									)
							)}
						</Menu>
					</PrimaryMenu>
					<SearchAndUserMenu>
						{isDesktop && (
							<SearchTrigger
								small={hasCustomLink}
								isSearchOpen={isSearchOpen}
								onClick={openSearch}
								aria-expanded={isSearchOpen ? 'true' : 'false'}
							>
								<Icon name="search" size="medium" />
								<SearchTriggerPlaceholder>
									{t('navbar_search')}
								</SearchTriggerPlaceholder>
							</SearchTrigger>
						)}
						{!isDesktop && (
							<MobileSearchButton
								isSearchOpen={isSearchOpen}
								title={t('navbar_search')}
								onClick={openSearch}
								tabIndex={isSearchOpen ? '-1' : null}
							>
								<Icon name="search" size="medium" />
							</MobileSearchButton>
						)}
						{isDesktop && hasCustomLink && (
							<EasyTooltip
								content={isCustomTitleLong && customLink.title}
							>
								<CustomLinkTab
									forwardedAs="a"
									href={getUrlServiceHref(customLink.url)}
									icon="external"
									aria-label={
										isCustomTitleLong
											? customLink.title
											: undefined
									}
								>
									{!isCustomTitleLong && customLink.title}
								</CustomLinkTab>
							</EasyTooltip>
						)}
						<nav aria-label={t('secondary_navigation')}>
							<UserMenu />
						</nav>
					</SearchAndUserMenu>
					<SearchBackground isSearchOpen={isSearchOpen} />
				</HeaderContent>
				<SearchForm
					isOpen={isSearchOpen}
					onOpen={openSearch}
					onClose={closeSearch}
					searchInputRef={searchInputRef}
					height={COLLAPSED_NAVBAR_HEIGHT}
				/>
			</CollapsibleHeader>
		</ThemeSection>
	);
}

export default Navigation;
